import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, timeout, catchError, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MenuService } from './menu.service';
import { NavItem } from '../layouts/full/vertical/sidebar/nav-item/nav-item';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/auth`;
  private apiUrlPass = `${environment.apiUrl}/users`;
  private currentUserSubject: BehaviorSubject<any | null>;
  public currentUser: Observable<any | null>;

  constructor(
  private http: HttpClient, 
  private router: Router, 
  private menuService: MenuService,
  private storage: StorageService) {
    const storedUser = localStorage.getItem('currentUser');
    this.currentUserSubject = new BehaviorSubject<any | null>(storedUser ? JSON.parse(storedUser) : null);
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): any | null {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string, rememberMe: boolean = false): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/login`, { email, password, rememberMe }).pipe(
      tap((response: any) => {
        const decodedToken: any = jwtDecode(response.token);
        const user: any = {
          id: decodedToken.id,
          name: decodedToken.name,
          email: decodedToken.email,
          profile: decodedToken.profile,
          companies: decodedToken.clients, // Manejar array de compañías
          token: response.token,
          selectedCompany: decodedToken.clients.length === 1 ? decodedToken.clients[0] : null,
          exp: decodedToken.exp, // Añadir la fecha de expiración del token
          locales: response.user?.locales || [] // Locales asignados al usuario
        };
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
        const newMenuItems = this.getMenuItemsForUser(user);
        this.menuService.updateMenuItems(newMenuItems);
      })
    );
  }
  loginTecnico(rut: string, password: string, rememberMe: boolean = false): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/login_tecnico`, { rut, password, rememberMe }).pipe(
      tap((response: any) => {
        const decodedToken: any = jwtDecode(response.token);
        const user: any = {
          id: decodedToken.id,
          name: decodedToken.name,
          email: decodedToken.email,
          profile: decodedToken.profile,
          companies: decodedToken.clients, // Manejar array de compañías
          token: response.token,
          selectedCompany: decodedToken.clients.length === 1 ? decodedToken.clients[0] : null,
          exp: decodedToken.exp, // Añadir la fecha de expiración del token
          locales: response.user?.locales || [] // Locales asignados al usuario
        };
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
        const newMenuItems = this.getMenuItemsForUser(user);
        this.menuService.updateMenuItems(newMenuItems);
      })
    );
  }

  // Identificar si el usuario tiene restricciones.
  public get isRestrictedUser(): boolean {
    const user = this.currentUserValue;
    if (!user) return false;
    return user.profile !== 'admin' && 
           user.profile !== 'superadmin' && 
           user.locales && 
           user.locales.length > 0;
  }

  private getMenuItemsForUser(user: any): any[] {
    if (user.profile === 'admin' || user.profile === 'superadmin') {
      return this.adminNavItems; 
    } 
    
    let items = this.userNavItems;

    // Si tiene locales asignados, ocultar módulos
    if (this.isRestrictedUser) {
      items = items.filter(item => {
        const routeLower = (item.route || '').toLowerCase();
        return !routeLower.includes('presupuestos') && !routeLower.includes('reportes');
      });
    }

    return items;
  }

  logout(): void {
    // 1. Limpieza profunda del disco duro del navegador
    localStorage.clear();
    sessionStorage.clear();

    // 2. Limpieza de los servicios de Angular (BehaviorSubjects)
    this.storage.clear(); 
    this.currentUserSubject.next(null);
    this.menuService.updateMenuItems([]);

    // 3. FIX: Forzamos la recarga de la página para matar cualquier caché en la RAM de Angular
    window.location.href = '/auth/login';
  }
  
  isAuthenticated(): boolean {
    const user = this.currentUserValue;
    if (user && user.token) {
      const currentTime = Math.floor(Date.now() / 1000);
      return user.exp > currentTime; // Verificar si el token ha expirado
    }
    return false;
  }

  getToken(): string | null {
    return this.currentUserValue ? this.currentUserValue.token : null;
  }

  getUserProfile(): any | null {
    return this.currentUserValue;
  }
  /* @Patch(':id/password') */
  changePassword(id: number, password: string): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlPass}/${id}/password`, { password });
  }

  forgotPassword(email: string): Observable<any> {
    console.log('AuthService: Sending forgot password request to:', `${this.apiUrl}/forgot-password`);
    return this.http.post<any>(`${this.apiUrl}/forgot-password`, { email })
      .pipe(
        tap((response: any) => console.log('AuthService: Received response:', response)),
        timeout(10000),
        catchError((error: any) => {
          console.error('AuthService: Error in forgot password:', error);
          if (error.name === 'TimeoutError') {
            return throwError(() => new Error('La solicitud ha tardado demasiado tiempo. Por favor, intente nuevamente.'));
          }
          return throwError(() => error);
        })
      );
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reset-password`, { token, newPassword })
      .pipe(
        timeout(10000),
        catchError((error: any) => {
          if (error.name === 'TimeoutError') {
            throw new Error('La solicitud ha tardado demasiado tiempo. Por favor, intente nuevamente.');
          }
          throw error;
        })
      );
  }

  private adminNavItems: NavItem[] = [
    {
      displayName: 'Dashboard',
      iconName: 'dashboard',
      route: '/admin/dashboard',
    },
    {
      displayName: 'User Management',
      iconName: 'people',
      route: '/admin/users',
    },
    // Otros elementos específicos para administradores
  ];

  private userNavItems: NavItem[] = [
    {
      displayName: 'Home',
      iconName: 'home',
      route: '/home',
    },
    {
      displayName: 'Profile',
      iconName: 'person',
      route: '/profile',
    },
    // Otros elementos específicos para usuarios regulares
  ];
}
